import { ArrowRight, Sparkles, ArrowUpRight, ChevronDown } from 'lucide-react'
import Navigation from '@/components/feature/Navigation'
import Footer from '@/components/feature/Footer'
import NewsStrip from '@/components/feature/NewsStrip'
import ServerCard from '@/components/feature/ServerCard'
import DiscordCta from '@/components/feature/DiscordCta'
import VantaFog from '@/components/feature/VantaFog'
import AmbientEffects from '@/components/feature/AmbientEffects'
import SectionHeading from '@/components/feature/SectionHeading'
import { LinkButton } from '@/components/base/Button'
import CommunitySection from '@/pages/home/components/CommunitySection'
import GalleryTeaser from '@/pages/home/components/GalleryTeaser'
import TeamSection from '@/pages/home/components/TeamSection'
import ConductSection from '@/pages/home/components/ConductSection'
import ContactSection from '@/pages/home/components/ContactSection'
import serverConfig from '@/config/serverConfig'
import { useLanguage } from '@/localization'

/** Minecraft home page. One container width + one vertical rhythm across every
 *  section (hero → servers → community → discord → conduct → team → news →
 *  contact → final CTA). Route: "/" */
export default function HomeModern() {
  const { vanilla, modded } = serverConfig.servers
  const { t } = useLanguage()

  return (
    <div className="relative flex min-h-screen flex-col bg-[#f4f7f2]">
      {/* Atmospheric light-green smoke backdrop. Fixed z-0 fog bleeds up through
          the hero's mask-to-transparent bottom; soft white motes drift over it.
          Both self-suppress under prefers-reduced-motion. */}
      <VantaFog className="!fixed" />
      <AmbientEffects />

      <div className="relative z-10 flex min-h-screen flex-col">
        <Navigation />

        {/* Hero — cross-game banner, Minecraft-skinned. Self-contained: it ends
            cleanly and the masked key art dissolves into the smoke below. */}
        <section className="relative flex min-h-[calc(100vh_-_4rem)] flex-col items-center justify-center overflow-hidden px-4 pb-20 pt-16 text-center">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(/brand/mc-hero.png)',
              maskImage: 'linear-gradient(to bottom, black 55%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to bottom, black 55%, transparent 100%)',
            }}
          />

          <div className="relative z-10 flex max-w-3xl flex-col items-center">
            <img
              src="/brand/ul-logo.png"
              alt="Uliunai Minecraft"
              className="h-24 w-24 animate-float object-contain drop-shadow-2xl md:h-32 md:w-32"
            />

            <h1 className="mt-2 bg-gradient-to-b from-grass via-grass-deep to-slate-deep bg-clip-text font-pixel text-3xl leading-tight tracking-tight text-transparent drop-shadow-[0_3px_0_rgba(255,255,255,0.55)] sm:text-5xl md:text-6xl">
              MINECRAFT
            </h1>

            <div className="mt-5 flex items-center justify-center gap-3">
              <span className="h-[3px] w-10 bg-gradient-to-r from-transparent to-grass-dark sm:w-16" />
              <span className="font-pixel text-[10px] uppercase tracking-[0.25em] text-grass-deep sm:text-xs">
                {t('Uliunai Servers')}
              </span>
              <span className="h-[3px] w-10 bg-gradient-to-l from-transparent to-grass-dark sm:w-16" />
            </div>

            <p className="mt-5 max-w-2xl text-lg font-semibold text-slate-deep drop-shadow-[0_1px_2px_rgba(255,255,255,0.6)] sm:text-2xl">
              {t('Play Survival now, with a separate curated Modded adventure planned for Java Edition.')}
            </p>

            <span className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/90 px-4 py-1 text-sm font-bold text-grass-deep shadow-block">
              <Sparkles size={16} /> {t('Survival server live now')}
            </span>

            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <LinkButton to="/servers" variant="primary" size="lg">
                {t('Explore the servers')} <ArrowRight size={18} />
              </LinkButton>
              <LinkButton to="/mods?server=modded" variant="secondary" size="lg">
                {t('See the modpack')}
              </LinkButton>
            </div>
          </div>

          {/* Scroll cue — mirrors the KF1 hero's down-arrow */}
          <button
            onClick={() => document.getElementById('mc-servers')?.scrollIntoView({ behavior: 'smooth' })}
            className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 animate-bounce cursor-pointer text-grass-deep transition-colors hover:text-grass-dark"
            aria-label={t('Scroll to servers')}
          >
            <ChevronDown size={28} />
          </button>
        </section>

        {/* Servers */}
        <section id="mc-servers" className="mx-auto w-full max-w-6xl px-4 py-16 sm:py-20">
          <SectionHeading
            eyebrow="Our servers"
            title="Two worlds, one community"
            subtitle="Pick Survival or a curated modded adventure — both run by the same friendly Uliunai crew."
          />
          <div className="grid gap-6 md:grid-cols-2">
            <ServerCard server={vanilla} kind="vanilla" />
            <ServerCard server={modded} kind="modded" />
          </div>
        </section>

        {/* Gallery teaser — real screenshots, links to /gallery */}
        <GalleryTeaser />

        {/* Who we are — community story + value props */}
        <CommunitySection />

        {/* Discord */}
        <DiscordCta />

        {/* Universal code of conduct */}
        <ConductSection />

        {/* Core team (grass-skinned rich cards) */}
        <TeamSection />

        {/* News */}
        <NewsStrip />

        {/* Contact */}
        <ContactSection />

        {/* Final CTA */}
        <section className="mx-auto w-full max-w-6xl px-4 py-16 sm:py-20">
          <div className="relative overflow-hidden rounded-3xl bg-grass-gradient p-10 text-center text-white shadow-block-lg md:p-14">
            <div className="absolute inset-0 block-dots opacity-30" />
            <div className="relative">
              <h2 className="mb-3 text-3xl font-extrabold md:text-4xl">{t('Ready to play?')}</h2>
              <p className="mx-auto mb-6 max-w-xl text-lg text-white/90">
                {t('Survival is online now at')}{' '}
                <span data-no-translate>{vanilla.host}</span>
                {'. '}
                {t('No client mods or modpack are required.')}
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <LinkButton to="/join" variant="secondary" size="lg">
                  {t('How to join')}
                </LinkButton>
                <LinkButton to="/mods?server=vanilla" variant="wood" size="lg">
                  {t('Browse plugins')}
                </LinkButton>
              </div>
              <a
                href="https://uliunai.com/#community"
                className="mt-8 inline-flex items-center gap-1.5 text-sm font-bold text-white/90 underline-offset-4 transition-colors hover:text-white hover:underline"
              >
                {t('Part of the Uliunai community - learn more')} <ArrowUpRight size={15} />
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </div>
  )
}
